import { FC, useEffect, useState } from "react";
import { Box, Container, Link, Typography } from "@mui/material";
import "../assets/styles/Footer.css";
import { Component, ModuleComponents } from "../types/ContentStructure";
import { getFooter } from "../services/ContentServices";
import ComponentType from "../components/component_type/ComponentType";

// ─────────────────────────────────────────────────────────────────────────────
// Footer — Layout por índice, con normalización del orden.
//
// Sub-columnas de Trasparencia usan gridAutoFlow='column':
//   col 1: UNE, Fondo Protección, Reportes Fin.
//   col 2: Aviso Legal, Aviso Privacidad, Comité de remun.
// ─────────────────────────────────────────────────────────────────────────────

interface SectionContentProps {
    component: Component;
    centered?: boolean;
    isAnchor?: boolean;
}

const SectionContent: FC<SectionContentProps> = ({ component, centered = false, isAnchor = false }) => {
    const { title, image, link, components } = component;

    const isIconRow = components.length > 0 && components.every(c =>
        c.componentType.name === 'LINK' && c.icon?.startsWith('http')
    );

    // Sub-columnas: col derecha (no ancla) con 4+ hijos de texto
    const useSubCols = !isAnchor && centered && components.length >= 4;

    // Filas por columna para gridAutoFlow='column': ceil(n/2)
    const rowsPerCol = Math.ceil(components.length / 2);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: centered ? 'center' : 'flex-start' },
            textAlign: { xs: 'left', md: centered ? 'center' : 'left' },
            height: '100%',
        }}>

            {/* Logo */}
            {image && (
                <Link href={link ?? '#'} target="_blank" rel="noopener noreferrer" sx={{ mb: 3, display: 'block' }}>
                    <img
                        src={image}
                        alt={title ?? ''}
                        style={{ width: 220, maxHeight: 190, objectFit: 'contain', display: 'block' }}
                    />
                </Link>
            )}

            {/* Título de sección */}
            {title && (
                <Typography variant="h3" component="h3" sx={{ mb: 2.5, color: 'inherit' }}>
                    {title}
                </Typography>
            )}

            {/* Hijos — 3 casos */}
            {isIconRow ? (
                // RRSS: íconos en fila horizontal
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5 }}>
                    {components.map((item, idx) => (
                        <ComponentType key={idx} component={item} />
                    ))}
                </Box>

            ) : useSubCols ? (
                // Sub-columnas con flujo por columna (Trasparencia desktop/tablet)
                // Mobile: oculto, se muestra el bloque de abajo
                <>
                    {/* Desktop/Tablet — grid 2 cols por columna */}
                    <Box sx={{
                        display: { xs: 'none', md: 'grid' },
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridTemplateRows: `repeat(${rowsPerCol}, auto)`,
                        gridAutoFlow: 'column',
                        columnGap: 4,
                        rowGap: 1,
                        width: '100%',
                        justifyItems: 'center',

                    }}>
                        {components.map((item, idx) => (
                            <ComponentType key={idx} component={item} />
                        ))}
                    </Box>
                    {/* Mobile — lista simple 1 columna */}
                    <Box sx={{
                        display: { xs: 'flex', md: 'none' },
                        flexDirection: 'column',
                        gap: 0.75,
                        width: '100%',
                    }}>
                        {components.map((item, idx) => (
                            <ComponentType key={idx} component={item} />
                        ))}
                    </Box>
                </>

            ) : (
                // 1 columna simple (ancla izq o pocas secciones)
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    alignItems: { xs: 'flex-start', md: centered ? 'center' : 'flex-start' },
                    width: '100%',
                }}>
                    {components.map((item, idx) => (
                        <ComponentType key={idx} component={item} sx={{ maxWidth: { xs: '100%', md: '32ch' } }} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

// ─── Normalizar orden: Por orden de componente ───
function normalizeFooterOrder(comps: Component[]): Component[] {
    return [...comps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function Footer() {
    const [footer, setFooter] = useState<Component>();

    useEffect(() => {
        const fetchData = async () => {
            const response = await getFooter();
            const footerData = response.data as ModuleComponents;
            setFooter(footerData.components[0].component);
        }
        fetchData();
    }, [])

    if (!footer || footer.components.length === 0) {
        return <Box className="Footer_Grid" sx={{ minHeight: 200 }} />;
    }

    const comps = normalizeFooterOrder(footer.components);

    return (
        <Box
            className="Footer_Grid"
            sx={[
                footer.theme.themeStyles,
                { pt: { xs: 5, md: 8 }, pb: { xs: 5, md: 8 } }
            ]}
        >
            <Container maxWidth="xl">

                {/* ── Mobile: 1 columna ── */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 5 }}>
                    {comps.map((comp, idx) => (
                        <SectionContent key={idx} component={comp} centered={false} isAnchor={!!comp.image} />
                    ))}
                </Box>

                {/* ── Desktop/Tablet: 3 columnas ── */}
                <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: 'repeat(3, 1fr)', columnGap: 8 }}>
                    {/* Columna 1 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {comps[0] && (
                            <SectionContent component={comps[0]} centered={true} isAnchor={!!comps[0].image} />
                        )}
                    </Box>
                    {/* Columna 2 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {comps[1] && (
                            <SectionContent component={comps[1]} centered={true} isAnchor={!!comps[1].image} />
                        )}
                    </Box>
                    {/* Columna 3 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {comps.slice(2).map((comp, idx) => (
                            <SectionContent key={`col3-${idx}`} component={comp} centered={true} isAnchor={!!comp.image} />
                        ))}
                    </Box>
                </Box>

            </Container>
        </Box>
    );
}

export default Footer;
